import { useEffect } from "react";

if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  try {
    window.history.scrollRestoration = "manual";
  } catch {
    // silent: some browsers (private mode, locked-down embeds)
    // refuse to mutate history.scrollRestoration — non-critical,
    // browser default ('auto') just means slight scroll flash on back.
  }
}

/**
 * Imperatively persist the current scrollY under `key`.
 *
 * Use case: just before navigating away (button onClick → nav("/")) we want
 * to guarantee the latest position is saved without waiting on the 100ms
 * throttle inside useScrollRestore's scroll listener. The hook's unmount
 * already flushes, but tapping a link mid-scroll may race the throttle.
 *
 * Pure function — does NOT require the hook to be mounted, but in practice
 * pairs with it (the matching mount call reads back the value).
 */
export function saveScrollPosition(key: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(key, String(window.scrollY));
  } catch {
    // silent: sessionStorage unavailable (incognito mode / quota exceeded)
    // — scroll restoration just won't happen, no user-visible failure.
  }
}

/**
 * Постранично сохраняет window.scrollY (по событию scroll) в sessionStorage
 * под уникальным ключом и восстанавливает при mount. Дожидается, пока контент
 * отрендерится и страница станет высокой.
 */
export function useScrollRestore(key: string): void {
  // Restore on mount
  useEffect(() => {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(key);
    } catch {
      return;
    }
    if (!raw) return;
    const y = parseInt(raw, 10);
    if (isNaN(y) || y <= 0) return;

    let attempts = 0;
    const maxAttempts = 40; // ~2s
    let cancelled = false;
    let timer: number | null = null;

    // Если юзер начал скроллить вручную во время восстановления — отменяем,
    // чтобы не дёргать его обратно к сохранённой позиции.
    const userInterruption = () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
    };
    window.addEventListener("touchstart", userInterruption, { passive: true, once: true });
    window.addEventListener("wheel", userInterruption, { passive: true, once: true });
    window.addEventListener("keydown", userInterruption, { once: true });

    const tick = () => {
      if (cancelled) return;
      const docH = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight
      );
      const viewH = window.innerHeight;
      const reachable = Math.max(0, docH - viewH);
      if (reachable >= y || attempts >= maxAttempts) {
        window.scrollTo({ top: y, behavior: "auto" });
        timer = window.setTimeout(() => {
          if (!cancelled) window.scrollTo({ top: y, behavior: "auto" });
        }, 60);
        return;
      }
      attempts += 1;
      timer = window.setTimeout(tick, 50);
    };

    timer = window.setTimeout(tick, 0);

    return () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
      window.removeEventListener("touchstart", userInterruption);
      window.removeEventListener("wheel", userInterruption);
      window.removeEventListener("keydown", userInterruption);
    };
  }, [key]);

  // Save continuously
  useEffect(() => {
    let throttle: number | null = null;
    const saveNow = () => {
      throttle = null;
      try {
        const y = window.scrollY;
        if (y > 0) {
          sessionStorage.setItem(key, String(y));
        } else {
          sessionStorage.removeItem(key);
        }
      } catch {
        // silent: sessionStorage unavailable (incognito mode / quota
        // exceeded) — we lose scroll restoration for this session,
        // not worth surfacing to the user.
      }
    };
    const onScroll = () => {
      if (throttle != null) return;
      throttle = window.setTimeout(saveNow, 100);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      // Flush pending throttle и сохранить последнюю позицию при unmount,
      // иначе при быстром тапе на ссылку прокрученная позиция теряется.
      if (throttle != null) {
        window.clearTimeout(throttle);
        saveNow();
      }
    };
  }, [key]);
}
